import RESTModelNormalizationSchemas from '../../../redux/models/RESTModelNormalizationSchemas'
import StoreDeviceCoordinate from '../../../redux/models/DeviceCoordinate'
import {
  findDeviceIdForCoordinateUuid,
  buildDeviceWithPrependedCoordinateId,
  parseCoordinatesCreatedPayload,
  buildCoordinateCreatedMerge
} from './coordinatesCreatedMessage'

/**
 * Merges normalized entity maps (later keys overwrite earlier).
 * @param {Record<string, Record<string, unknown>>} target
 * @param {Record<string, Record<string, unknown>> | null | undefined} source
 */
export function mergeEntityMaps (target, source) {
  if (!source) return
  for (const entityName of Object.keys(source)) {
    const slice = source[entityName]
    if (!slice || typeof slice !== 'object') continue
    if (!target[entityName]) target[entityName] = {}
    Object.assign(target[entityName], slice)
  }
}

/**
 * Same merge logic as the map socket `map:device:position` handler (normalized REST coordinate).
 * @param {object} devicesById - `state.data.devices`
 * @param {object} payload - `{ coordinate }` or coordinate-shaped object
 * @returns {object|null} entities map for mergeEntitiesIntoStores
 */
export function buildMapPositionMergeEntities (devicesById, payload) {
  const coordinate = { ...(payload?.coordinate ?? payload) }
  if (!coordinate?.id) return null
  if (coordinate.deviceId != null) {
    const resolved = findDeviceIdForCoordinateUuid(devicesById, coordinate.deviceId)
    if (resolved) coordinate.deviceId = resolved
  }
  const storeCoord = StoreDeviceCoordinate.FromNormalizedRESTModel(coordinate)
  const entities = {
    [RESTModelNormalizationSchemas.deviceCoordinate.entityName]: {
      [storeCoord.id]: storeCoord
    }
  }
  const storeDevice = buildDeviceWithPrependedCoordinateId(
    devicesById,
    storeCoord.deviceId,
    storeCoord.id
  )
  if (storeDevice && storeCoord.deviceId) {
    entities[RESTModelNormalizationSchemas.device.entityName] = {
      [storeCoord.deviceId]: storeDevice
    }
  }
  return entities
}

/**
 * Buffer key for coalescing position updates (last payload per device per animation frame).
 * @returns {string|null}
 */
export function getBufferedDeviceKeyForPositionPayload (devicesById, payload) {
  const coordinate = { ...(payload?.coordinate ?? payload) }
  if (!coordinate?.id) return null
  let deviceId = coordinate.deviceId
  if (deviceId != null) {
    const resolved = findDeviceIdForCoordinateUuid(devicesById, deviceId)
    if (resolved) deviceId = resolved
  }
  if (deviceId != null) return String(deviceId)
  return `coord:${coordinate.id}`
}

/**
 * @param {unknown} raw - legacy tuple or object (see parseCoordinatesCreatedPayload)
 * @returns {string|null}
 */
export function getBufferedDeviceKeyForLegacyPayload (devicesById, raw) {
  const payload = parseCoordinatesCreatedPayload(raw)
  if (!payload) return null
  const uuid = payload.uuid_device ??
    payload.uuidDevice ??
    payload.deviceId ??
    payload.device_id ??
    payload.uuid_tracker ??
    payload.trackerUuid ??
    payload.tracker_uuid ??
    payload.trackerId ??
    payload.tracker_id
  const deviceId = findDeviceIdForCoordinateUuid(devicesById, uuid)
  return deviceId != null ? String(deviceId) : null
}

/**
 * Legacy "coordinates created" socket payload → entities map.
 * @returns {object|null}
 */
export function buildLegacyCoordinateMergeEntities (devicesById, raw) {
  const payload = parseCoordinatesCreatedPayload(raw)
  if (!payload) return null
  const merge = buildCoordinateCreatedMerge(devicesById, payload)
  if (!merge) return null
  return {
    [RESTModelNormalizationSchemas.deviceCoordinate.entityName]: {
      [merge.coordId]: merge.storeCoord
    },
    [RESTModelNormalizationSchemas.device.entityName]: {
      [merge.deviceId]: merge.storeDevice
    }
  }
}
